import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { formatDate } from "@/lib/utils";

export default function Approvals() {
  const [jobs, setJobs] = useState([]);
  const [confirmingId, setConfirmingId] = useState(null); // job pending reject confirmation
  const load = async () => {
    try {
      const { data } = await api.get("/jobs");
      setJobs(data.filter((j) => j.status === "needs_approval"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      await api.patch(`/jobs/${id}/approve`);
      toast.success("Job approved & activated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reject = async (id) => {
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Request rejected & removed");
      setConfirmingId(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="approvals-page">
      <h1 className="brand-title text-2xl md:text-3xl mb-1">Pending Approvals</h1>
      <p className="text-white/60 text-sm mb-6">Client-submitted job requests waiting for review.</p>

      <div className="grid gap-4">
        {jobs.length === 0 && <p className="text-white/50">No pending requests.</p>}
        {jobs.map((j) => (
          <div key={j.id} className="rounded-2xl p-5 border border-white/10 bg-white/[0.03]" data-testid={`approval-${j.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="brand-title text-lg text-[#19e6c8]">{j.job_ref}</h3>
                <p className="text-white/60 text-xs">{formatDate(j.job_date)} · Submitted by client</p>
              </div>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "#f59e0b22", color: "#f59e0b" }}>
                <span className="dot" style={{ background: "#f59e0b" }} /> Needs Approval
              </span>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
              <div><p className="text-xs text-white/50 uppercase tracking-widest">Pickup</p><p className="text-white">{j.pickup_address}</p></div>
              <div><p className="text-xs text-white/50 uppercase tracking-widest">Drop-off</p><p className="text-white">{j.dropoff_address}</p></div>
              <div className="text-white/70 sm:col-span-2">{j.item_qty} × {j.item_type} · {j.volume_cbm} cbm · {j.weight_kgs} kgs</div>
              {j.notes && <div className="sm:col-span-2 text-white/70 italic">"{j.notes}"</div>}
            </div>
            {confirmingId === j.id ? (
              <div className="mt-4 rounded-xl border border-[#ef4444]/30 bg-[#ef4444]/10 p-3 flex flex-wrap items-center gap-3" data-testid={`reject-confirm-${j.id}`}>
                <span className="flex items-center gap-2 text-sm text-[#fca5a5]">
                  <AlertTriangle size={15} /> Reject this request? It will be permanently deleted.
                </span>
                <div className="flex gap-2 ml-auto">
                  <button onClick={() => setConfirmingId(null)} className="px-4 py-2 rounded-lg text-sm font-semibold bg-white/10 hover:bg-white/15 text-white/80" data-testid={`reject-cancel-${j.id}`}>
                    Cancel
                  </button>
                  <button onClick={() => reject(j.id)} className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2" style={{ background: "#ef4444", color: "#fff" }} data-testid={`reject-confirm-btn-${j.id}`}>
                    <XCircle size={14} /> Confirm Rejection
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex gap-2">
                <button onClick={() => approve(j.id)} className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2" style={{ background: "#22c55e22", color: "#22c55e" }} data-testid={`approve-${j.id}`}>
                  <CheckCircle2 size={14} /> Approve
                </button>
                <button onClick={() => setConfirmingId(j.id)} className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2" style={{ background: "#ef444422", color: "#ef4444" }} data-testid={`reject-${j.id}`}>
                  <XCircle size={14} /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
